import { useLocalStorage } from './useLocalStorage';

export type UserStatus = 
  | 'available'
  | 'away'
  | 'be-right-back'
  | 'busy'
  | 'on-the-phone'
  | 'out-to-lunch'
  | 'invisible'
  | 'custom';

export interface UserStatusState {
  status: UserStatus;
  customMessage: string;
  idleSince: number | null;
}

export const STATUS_CONFIG: Record<UserStatus, { label: string; icon: string; color: string }> = {
  'available': { label: 'Available', icon: '🟢', color: 'text-green-600 dark:text-green-400' },
  'away': { label: 'Away', icon: '🟡', color: 'text-yellow-500' },
  'be-right-back': { label: 'Be Right Back', icon: '🟡', color: 'text-yellow-500' },
  'busy': { label: 'Busy', icon: '🔴', color: 'text-red-500' },
  'on-the-phone': { label: 'On The Phone', icon: '🔴', color: 'text-red-500' },
  'out-to-lunch': { label: 'Out To Lunch', icon: '🟡', color: 'text-yellow-500' },
  'invisible': { label: 'Invisible', icon: '⚫', color: 'text-gray-400' },
  'custom': { label: 'Custom', icon: '💬', color: 'text-blue-500' },
};

const DEFAULT_STATUS: UserStatusState = {
  status: 'available',
  customMessage: '',
  idleSince: null,
};

export function useUserStatus() {
  const [statusState, setStatusState] = useLocalStorage<UserStatusState>(
    'buddy-chat:user-status',
    DEFAULT_STATUS
  );

  const setStatus = (status: UserStatus, customMessage?: string) => {
    setStatusState({
      status,
      customMessage: customMessage || (status === 'custom' ? statusState.customMessage : ''),
      idleSince: status === 'away' ? Date.now() : null,
    });
  };

  const setCustomMessage = (message: string) => {
    setStatusState({
      ...statusState,
      customMessage: message,
    });
  };

  const getStatusDisplay = () => {
    const config = STATUS_CONFIG[statusState.status];
    if (statusState.status === 'custom' && statusState.customMessage) {
      return statusState.customMessage;
    }
    return config.label;
  };

  return {
    ...statusState,
    setStatus,
    setCustomMessage,
    getStatusDisplay,
    statusConfig: STATUS_CONFIG[statusState.status],
  };
}
